import { useRateFieldError } from "../../hooks/useRateFieldError";
import { FC, useCallback, useEffect, useMemo } from "react";

import { Stack } from "@mui/material";
import { useFormContext, useWatch } from "react-hook-form";

import { useRateContext } from "../../Context/RateContext";
import { useRateFieldName } from "../../hooks/useRateFieldName";

import { trpc } from "@/common/helpers/trpc";
import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardAutocompleteV2 } from "@/dashboard-design-system/DashboardAutocompleteV2";
import { AutocompleteV2Option } from "@/dashboard-design-system/DashboardAutocompleteV2/types";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
import { StepTicketsFormData } from "@/features/LiveFormEditor/Steps/StepTickets/types";

interface MemberOnlyMembershipsFieldProps {
  variant?: "default" | "inline";
}

export const MemberOnlyMembershipsField: FC<MemberOnlyMembershipsFieldProps> = ({ variant = "default" }) => {
  const { t } = useTranslate();
  const { rateIndex } = useRateContext();
  const { control, setValue, register, unregister } = useFormContext<StepTicketsFormData>();

  const fieldName = useRateFieldName("memberOnlyMembershipRateIds");

  useEffect(() => {
    register(fieldName, {
      validate: (value, formValues) =>
        !formValues.rates?.[rateIndex]?.isMemberOnly ||
        Boolean(value?.length) ||
        t("dashboard", "ticketing.memberOnly.selectAtLeastOne"),
    });
    return () => unregister(fieldName, { keepValue: true, keepDefaultValue: true });
  }, [fieldName, rateIndex, register, unregister, t]);

  const selectedIds = useWatch({ control, name: fieldName });
  const error = useRateFieldError("memberOnlyMembershipRateIds");

  const { data: membershipRates } = trpc.getMembershipTypes.useQuery({});

  const options: AutocompleteV2Option<string>[] = useMemo(
    () => (membershipRates ?? []).map((m) => ({ label: m.name, value: m.rateId, secondaryLabel: m.campaignName })),
    [membershipRates]
  );

  const selectedOptions = useMemo(
    () => options.filter((opt) => selectedIds?.includes(opt.value)),
    [options, selectedIds]
  );

  const handleChange = useCallback(
    (value: AutocompleteV2Option<string>[] | undefined) => {
      setValue(
        fieldName,
        (value ?? []).map((opt) => opt.value),
        { shouldDirty: true, shouldValidate: true }
      );
    },
    [fieldName, setValue]
  );

  const autocomplete = (
    <DashboardAutocompleteV2
      options={options}
      value={selectedOptions}
      onChange={handleChange}
      multiple
      selectAll
      selectAllLabel={t("dashboard", "ticketing.memberOnly.selectAll") ?? ""}
      allSelectedLabel={t("dashboard", "ticketing.memberOnly.allMembershipTypes") ?? ""}
      size={variant === "inline" ? "small" : "medium"}
      optionSize={variant === "inline" ? "small" : "standard"}
      placeholder={t("dashboard", "ticketing.memberOnly.selectMembershipType") ?? ""}
      isOptionEqualToValue={(option, value) => option.value === value.value}
      error={Boolean(error)}
      errorMessage={variant === "inline" ? undefined : error?.message}
    />
  );

  if (variant === "inline") {
    return (
      <Stack direction="row" spacing={1} alignItems="center" sx={{ width: { xs: "100%", sm: "auto" }, minWidth: 0 }}>
        <DashboardTypography variant="caption" vibe="text-neutral-quiet" sx={{ whiteSpace: "nowrap", flexShrink: 0 }}>
          {t("dashboard", "ticketing.memberOnly.appliesTo")}
        </DashboardTypography>
        <Stack sx={{ flexGrow: 1, minWidth: 0, width: { sm: 240 }, maxWidth: 240 }}>{autocomplete}</Stack>
      </Stack>
    );
  }

  return (
    <Stack spacing={0.5} sx={{ pt: 0.5, marginLeft: 3 }}>
      <DashboardTypography variant="body2" vibe="text-neutral-moderate">
        {t("dashboard", "ticketing.memberOnly.appliesTo")}
      </DashboardTypography>
      {autocomplete}
    </Stack>
  );
};
